import { useRouter } from 'next/router'

const GuidPattern =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}'

type RouteMatchOptions = {
  /**
   * A regex pattern to replace route variables
   * @default GuidPattern
   */
  variablePattern?: string
  /**
   * If true, the route must match exactly, including the end of the path.
   * @default false
   */
  exactMatch?: boolean
}

/**
 * This hook check if the current route matches any of the provided routes.
 *
 * @param routes - An array of routes to match against the current path.
 * @param routesMatchOptions - Options for matching routes, including variable pattern and exact match.
 * @returns A boolean indicating if the current route matches any of the provided routes.
 */
export default function useRouteMatch(
  routes: string[],
  routesMatchOptions?: RouteMatchOptions,
) {
  const router = useRouter()

  const variablePattern = routesMatchOptions?.variablePattern ?? GuidPattern
  const exactMatch = routesMatchOptions?.exactMatch ?? false

  return routes.some(candidateRoute => {
    if (candidateRoute === '/') {
      return router.asPath === '/'
    }

    const candidateRoutePattern = candidateRoute
      // Replace all variables with the provided pattern
      .replace(/\[[a-zA-Z_$][0-9a-zA-Z_$]*\]/g, variablePattern)
      // Replace all * with regex
      .replace(/\*/g, '.*')

    // Add start and end anchors to the pattern
    const candidateRouteRegexPattern = `^${candidateRoutePattern}${exactMatch ? '$' : ''}`

    const candidateRouteRegexExp = new RegExp(candidateRouteRegexPattern)

    return candidateRouteRegexExp.test(router.asPath)
  })
}
